import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button3D } from "./ui/button3d";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Track", path: "/track" },
  { label: "Packages", path: "/packages" },
  { label: "Gallery", path: "/gallery" },
  { label: "FOOD COURT", path: "https://namaste-south.ej6980.workers.dev/", isExternal: true },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = useCallback(
    (link) => {
      if (link.path === "/") return location.pathname === "/";
      if (link.path.startsWith("/#")) return location.pathname === "/" && location.hash === link.path.substring(1);
      return location.pathname === link.path;
    },
    [location]
  );

  const handleNavClick = useCallback(
    (link) => {
      setMobileOpen(false);
    },
    []
  );

  return (
    <>
      <motion.nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-black/90 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3"
          >
            <img 
              src="/site-logo.png" 
              alt="Big Bang Speedway" 
              className="h-[5.5rem] w-auto"
            />
            <span className="font-heading text-[#f5f5f5] text-2xl tracking-wider hidden sm:inline">
              BIG BANG<span className="text-[#e8001d]"> SPEEDWAY</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {NAV_LINKS.map((link) => {
              const isActiveLink = isActive(link);
              return link.isExternal ? (
                <Link
                  key={link.label}
                  to={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  onClick={() => handleNavClick(link)}
                >
                  <Button3D variant="red" size="sm">
                    {link.label}
                  </Button3D>
                </Link>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  onClick={() => handleNavClick(link)}
                  className="block"
                >
                  <Button3D
                    variant={isActiveLink ? "red" : "nav"}
                    size="sm"
                  >
                    {link.label}
                  </Button3D>
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-4">
            <Link to="/booking" data-testid="book-now-button" className="block">
              <Button3D variant="red" size="sm">
                Book Now
              </Button3D>
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-[#f5f5f5] p-2"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {NAV_LINKS.map((link, i) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                {link.isExternal ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    onClick={() => handleNavClick(link)}
                    className="font-heading text-4xl text-[#f5f5f5] hover:text-[#e8001d] transition-colors uppercase tracking-wider"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    onClick={() => handleNavClick(link)}
                    className="font-heading text-4xl text-[#f5f5f5] hover:text-[#e8001d] transition-colors uppercase tracking-wider"
                  >
                    {link.label}
                  </Link>
                )}
              </motion.div>
            ))}
            <Link to="/booking" data-testid="mobile-book-now-button" className="block mt-4">
              <Button3D variant="red" size="md">
                Book Now
              </Button3D>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
